// @flow
import * as React from 'react';
import cx from 'classnames';

import Button from '../../button';
import Link from '../../link';
import { getLocale } from '../../i18n';
import settings from '../../settings';

import Field from './field';
import Close from './close.svg';
import ExclamationCircle from './exclamation-circle.svg';
import Select from './select';
import { submitFeedback } from './submit';
import cs from './styles.pcss';
import t from './locale';

const validateEmail = (str) => /^[^\s]+@[^\s]+\.[^\s]{2,}$/.test(str);

type Props = {
  onClose: () => void,
};

export default function FeedbackForm({ onClose }: Props): React.Node {
  const [name, setName] = React.useState('');

  const [email, setEmail] = React.useState('');
  const [emailError, setEmailError] = React.useState(false);

  const [country, setCountry] = React.useState(null);
  const [countryError, setCountryError] = React.useState(false);

  const [subject, setSubject] = React.useState(null);
  const [subjectError, setSubjectError] = React.useState(false);

  const [message, setMessage] = React.useState('');
  const [messageError, setMessageError] = React.useState(false);

  const [loading, setLoading] = React.useState(false);
  const [success, setSuccess] = React.useState(false);
  const [submitError, setSubmitError] = React.useState(false);

  const resetErrors = () => {
    setEmailError(false);
    setCountryError(false);
    setSubjectError(false);
    setMessageError(false);
    setSubmitError(false);
  };

  const resetForm = () => {
    resetErrors();
    setEmail('');
    setCountry(null);
    setSubject(null);
    setMessage('');
  };

  const locale = getLocale();

  const handleSubmit = () => {
    resetErrors();
    let preventSubmit = false;

    const emailText = email.trim();
    if (!emailText || !validateEmail(emailText)) {
      setEmailError(true);
      preventSubmit = true;
    }

    if (!country) {
      setCountryError(true);
      preventSubmit = true;
    }

    if (!subject) {
      setSubjectError(true);
      preventSubmit = true;
    }

    const messageText = message.trim();
    if (!messageText) {
      setMessageError(true);
      preventSubmit = true;
    }

    if (preventSubmit) {
      return;
    }

    setLoading(true);

    submitFeedback({
      contact: emailText,
      body: messageText,
      subject,
      country,
      selectedOptions: [subject],
      name,
    })
      .then(() => {
        setLoading(false);
        setSuccess(true);
        resetForm();
      })
      .catch(() => {
        setLoading(false);
        setSubmitError(true);
      });
  };

  return (
    <div className={cs.popup}>
      {/* Header */}
      <div className={cs.header}>
        <div className={cs.headerText}>{t('header')}</div>
        <button type="button" className={cs.close} onClick={onClose}>
          <Close />
        </button>
      </div>

      {/* Content */}
      <div className={cs.content}>
        {success ? (
          <div className={cs.successBlock}>
            <div className={cs.successIcon}>
              <div className={cs.successIconContent}>
                <svg
                  width="24"
                  height="17"
                  viewBox="0 0 24 17"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M1.5 7L9.375 14.875L22.5 1.75"
                    stroke="white"
                    strokeWidth="3"
                    strokeLinecap="round"
                    className={cs.mark}
                  />
                </svg>
              </div>
            </div>

            <div className={cs.successTitle}>{t('success.title')}</div>

            <div className={cs.successMessage}>
              <div>{t('success.message1')}</div>
              <div>{t('success.message2')}</div>
            </div>
          </div>
        ) : (
          <>
            <div className={cs.faq}>
              {`${t('description.first')} `}
              <Link area="feedback" noUnderline href={settings.helpCenter}>
                {t('description.linkHelpCenter')}
              </Link>
              {` ${t('description.or')} `}
              <Link area="feedback" noUnderline href={settings.faq(locale)}>
                {t('description.linkFAQ')}
              </Link>
              {!['ja', 'de', 'ko'].includes(locale) && '?'}
              {` ${t('description.second')}`}
              {['ja', 'ko'].includes(locale) && (
                <p>
                  {`${t('description.third')}`}
                  <Link
                    target="_blank"
                    href={settings.eportalLinks[locale]}
                    area="feedback"
                  >
                    {`${t('description.linkEportal')}`}
                  </Link>
                  {`${t('description.end')}`}
                </p>
              )}
              {['ja'].includes(locale) && (
                <p className={cs.notice}>
                  <span className={cs.text}>{`${t(
                    'description.holiday'
                  )}`}</span>
                  <Link
                    className={cs.link}
                    target="_blank"
                    href={settings.feedbackLinks[locale].holiday}
                    area="feedback"
                  >
                    {`${t('description.linkEportal')}`}
                  </Link>
                  <span className={cs.text}>{`${t(
                    'description.holidayEnd'
                  )}`}</span>
                </p>
              )}
            </div>

            <hr className={cs.hr} />

            {/* Honeypot for bots */}
            <input
              type="text"
              id="name"
              name="name"
              placeholder="Your name"
              aria-label="Your name"
              autoComplete="nope"
              style={{ position: 'absolute', left: '-9000px' }}
              tabIndex="-1"
              value={name}
              onChange={setName}
            />

            <Field
              id="email"
              value={email}
              onChange={setEmail}
              error={emailError}
              element="input"
              disabled={loading}
            />

            <Field
              id="country"
              value={country}
              onChange={setCountry}
              error={countryError}
              element={Select}
              options={Object.entries(t('countries')).map(([value, label]) => ({
                label,
                value,
              }))}
              searchable
              disabled={loading}
            />

            <Field
              id="subject"
              value={subject}
              onChange={setSubject}
              error={subjectError}
              element={Select}
              options={Object.entries(t('options')).map(([value, label]) => ({
                label,
                value,
              }))}
              disabled={loading}
            />

            {subject === '4' && (
              <div className={cs.status}>
                <ExclamationCircle size={24} className={cs.icon} />
                <div>
                  {t('goToEbay.first')}{' '}
                  <Link
                    area="feedback"
                    noUnderline
                    href={settings.helpEbay(locale)}
                  >
                    {t('goToEbay.link')}
                  </Link>{' '}
                  {t('goToEbay.last')}
                </div>
              </div>
            )}

            <Field
              id="message"
              value={message}
              onChange={setMessage}
              error={messageError}
              element="textarea"
              maxLength={2500}
              disabled={loading}
            />

            {submitError && (
              <div className={cs.formError}>{t('submitError')}</div>
            )}
          </>
        )}
      </div>

      {/* Footer */}
      {success ? (
        <div className={cx(cs.footer, cs.footerRight)}>
          <Button size="small" mode="primary" color="blue" onClick={onClose}>
            {t('buttons.close')}
          </Button>
        </div>
      ) : (
        <div className={cs.footer}>
          <Button
            onClick={onClose}
            className={cs.footerButton}
            size="small"
            mode="secondary"
            color="blue"
          >
            {t('buttons.cancel')}
          </Button>
          <Button
            onClick={() => {
              handleSubmit();
            }}
            disabled={loading}
            loading={loading}
            className={cs.confirm}
            size="small"
            mode="primary"
            color="blue"
          >
            {t('buttons.submit')}
          </Button>
        </div>
      )}
    </div>
  );
}
